import { useEffect, useMemo, useState } from 'react';
import { mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOrg } from '@/features/team/OrgProvider';
import { usePeriod } from '@/features/period/PeriodProvider';
import { SelectorPeriodo } from '@/features/period/SelectorPeriodo';
import { useCola } from '@/core/offline/ColaProvider';
import { TarjetaPendiente } from './TarjetaPendiente';
import { formatCents, MESES } from '@/features/catalog/catalog';
import { WorkForm, type WorkFormMode } from './WorkForm';
import { WorkCard } from './WorkCard';
import { ConfirmarBorrado } from '@/ui/ConfirmarBorrado';
import { ConfirmarAccion } from '@/ui/ConfirmarAccion';
import { colorDe } from '@/ui/ColorParticipante';
import { AvisoFlotante } from '@/ui/AvisoFlotante';
import {
  deleteWork,
  FILTROS_VACIOS,
  voidWork,
  listWorks,
  setWorkStatus,
  type WorkListRow,
  listParticipants,
  type Participant,
  revertirFacturacion,
} from './works';

/**
 * Los trabajos de un mes.
 *
 * **El eje es el mes de FACTURACIÓN, no la fecha del trabajo.** Una visita del
 * 30 de agosto que se factura en septiembre aparece en septiembre. Son dos
 * campos distintos y confundirlos hace que un trabajo «desaparezca»: se lo
 * busca en el mes en que se hizo y está en el otro. La fecha del trabajo se
 * sigue viendo en la tarjeta, pero no decide dónde aparece.
 *
 * **Sin filtros.** La pantalla llegó a tener cuatro chips de estado, un
 * acordeón con seis campos y un modo de selección múltiple. Para el uso real
 * —cargar el trabajo, y llegado el momento ir marcando facturado y cobrado—
 * ninguno de los tres hacía falta, y entre todos tapaban lo único que importa,
 * que es la lista. Filtrar sigue existiendo en la capa de datos, y lo usan los
 * reportes; acá no.
 *
 * Quedan tres cosas, en este orden: cargar un trabajo, elegir el mes, y la
 * lista con su total. Nada en el medio.
 *
 * **Marcar facturado o cobrado no saca al trabajo de la lista.** Antes, con el
 * chip «Por facturar» puesto, marcarlo lo hacía desaparecer de la pantalla, que
 * es exactamente lo contrario de lo que uno espera al tildar algo de una lista.
 * El trabajo se queda donde está y cambia su estado a la vista.
 */
interface Props {
  /**
   * Abre el formulario de alta ya desplegado. Lo usa la acción principal de
   * Inicio: cargar un trabajo es lo primero que se hace al abrir la aplicación
   * en el campo, y llegar ahí no debería costar dos toques.
   */
  abrirNuevo?: boolean;
  /** Abre la ficha completa del trabajo. La resuelve quien contiene esta
   *  pantalla: es una pantalla propia, no un panel de acá adentro. */
  onVerDetalle: (id: string) => void;
}

/** Referencia estable: `?? []` crearía un array nuevo en cada render. */
const SIN_TRABAJOS: WorkListRow[] = [];

export function WorksPage({ abrirNuevo = false, onVerDetalle }: Props) {
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.organization_id;
  // El mismo período que Liquidación y el resumen. Uno solo, compartido: si
  // cada pantalla llevara el suyo, volver de una a otra cambiaría de mes sin
  // que nadie lo pida.
  const { year, month } = usePeriod();

  // Los trabajos cargados en el teléfono que el servidor todavía no confirmó.
  // Van en la MISMA lista que los demás: quien acaba de cargar uno lo busca
  // entre los del mes, no en un panel de sincronización aparte.
  const { altas, sincronizando, sincronizar, reintentar } = useCola();
  const sinServidor = useSinConexion();

  const queryClient = useQueryClient();

  const [errorAccion, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  // --- Formulario (alta, edición y duplicado comparten componente)
  const [form, setForm] = useState<{ mode: WorkFormMode; workId?: string } | null>(
    abrirNuevo ? { mode: 'create' } : null,
  );

  // Qué trabajo está esperando confirmación. El `confirm()` del navegador que
  // había antes se despacha sin leer —y en Android ni siquiera se ve igual—,
  // así que las confirmaciones son parte de la página.
  const [borrando, setBorrando] = useState<WorkListRow | null>(null);
  const [facturando, setFacturando] = useState<WorkListRow | null>(null);
  const [cobrando, setCobrando] = useState<WorkListRow | null>(null);
  const [revirtiendo, setRevirtiendo] = useState<WorkListRow | null>(null);
  const [motivoRevertir, setMotivoRevertir] = useState('');

  // Para pintar la barra del facturador. Si falla, la lista funciona igual: el
  // color es un atajo, nunca la única forma de saber quién factura.
  const [gente, setGente] = useState<Participant[]>([]);

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const l = await listParticipants(orgId);
        if (!cancelado) setGente(l);
      } catch {
        // Sin colores se ve igual de bien; no vale la pena molestar por esto.
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId]);

  // El único filtro que existe, y no es opcional: el mes de facturación.
  const cTrabajos = useQuery({
    queryKey: ['trabajos', orgId, year, month],
    queryFn: () => listWorks(orgId!, { ...FILTROS_VACIOS, year, month }),
    enabled: !!orgId,
  });

  const works: WorkListRow[] = cTrabajos.data ?? SIN_TRABAJOS;
  const cargando = cTrabajos.isPending;
  // El error de la consulta se mezcla con el de las acciones de la pantalla:
  // para quien mira, un problema es un problema.
  const error = errorAccion ?? (cTrabajos.error ? mensajeDeError(cTrabajos.error) : null);

  const recargar = () => {
    void queryClient.invalidateQueries({ queryKey: ['trabajos'] });
  };

  /** Envuelve una acción: apaga errores viejos, marca ocupado, recarga. */
  const hacer = async (accion: () => Promise<unknown>, exito?: string) => {
    setError(null);
    setAviso(null);
    setOcupado(true);
    try {
      await accion();
      if (exito) setAviso(exito);
      recargar();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  };

  /**
   * Anular deja el rastro. El motivo no es opcional: un trabajo que desaparece
   * de las cuentas sin explicación obliga, meses después, a reconstruir por qué.
   */
  const anular = async (id: string) => {
    const motivo = prompt(
      'Anular este trabajo lo saca de las cuentas pero lo conserva. ¿Por qué se anula?',
    );
    if (motivo === null) return;
    if (motivo.trim() === '') {
      setError('Hace falta un motivo para anular.');
      return;
    }
    await hacer(
      () => voidWork(id, motivo.trim()),
      'Trabajo anulado. Queda registrado con su motivo.',
    );
  };

  /**
   * Los trabajos cargados en el teléfono que todavía no subieron, de ESTE mes.
   *
   * Se filtran por su mes de facturación y no por el que esté abierto en
   * pantalla: un trabajo cargado sin señal para octubre no puede aparecer en
   * septiembre sólo porque septiembre es lo que se está mirando. Los que no
   * dicen a qué mes van —borradores de una versión anterior, que no guardaban
   * ese dato— se muestran igual: esconder un trabajo cargado es peor que
   * mostrarlo en el mes equivocado, porque desaparece sin dejar rastro.
   *
   * Los ya sincronizados quedan afuera: de ésos se encarga la lista de verdad,
   * y mostrarlos en los dos lados sería contar dos veces el mismo trabajo.
   */
  const enCola = useMemo(
    () =>
      altas.filter((a) => {
        if (a.estado === 'sincronizado') return false;
        const { billingYear: y, billingMonth: m } = a.resumen;
        if (y == null || m == null) return true;
        return y === year && m === month;
      }),
    [altas, year, month],
  );

  /**
   * Todavía no se sabe qué hay en este mes.
   *
   * Distinto de «no hay nada». Lo cargado en el teléfono SÍ se sabe siempre
   * —sale del propio dispositivo—, así que si hay algo en la cola la pantalla
   * ya tiene algo cierto que contar y no está esperando en blanco.
   */
  const esperandoLaLista = cargando && works.length === 0 && enCola.length === 0;

  const totales = useMemo(() => {
    // Un solo importe sin precio vuelve incompleto al total: no se suma como 0,
    // se suma lo determinado y se avisa que falta.
    const sinPrecio =
      works.filter((w) => w.price_pending).length +
      enCola.filter((a) => a.resumen.totalCents == null).length;
    // Los de la cola SUMAN. El trabajo se hizo; que el servidor todavía no lo
    // haya confirmado no lo vuelve menos real para quien lo cargó, y un total
    // que los ignora es un total que no cierra contra lo que se ve en la lista.
    const suma =
      works.reduce((acc, w) => acc + (w.total_cents ?? 0), 0) +
      enCola.reduce((acc, a) => acc + (a.resumen.totalCents ?? 0), 0);
    const facturados = works.filter((w) => w.is_billed).length;
    return { sinPrecio, suma, facturados };
  }, [works, enCola]);

  if (!activeOrg || !orgId) return null;

  if (form) {
    return (
      <WorkForm
        mode={form.mode}
        workId={form.workId}
        onSaved={(r) => {
          setForm(null);
          recargar();
          // Sólo se confirma lo que el servidor confirmó. Del trabajo que quedó
          // esperando NO se dice nada acá: lo cuenta el panel de sincronización,
          // que es el único que sabe cómo va. Un cartel fijo diciendo "se va a
          // enviar solo" seguiría ahí, dando tranquilidad, aunque el envío
          // hubiera fallado hace rato.
          setAviso(r.estado === 'sincronizado' ? 'Trabajo guardado.' : null);
        }}
        onCancel={() => setForm(null)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="works-page">
      <button
        type="button"
        onClick={() => setForm({ mode: 'create' })}
        className="min-h-14 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 font-medium text-[var(--sobre-accion)]"
        data-testid="new-work"
      >
        + Nuevo trabajo
      </button>

      <SelectorPeriodo testId="trabajos-periodo" />

      {/* Sin señal no se repite el aviso: ya lo dice la franja del marco, arriba
          de todo. Tres carteles para el mismo hecho —la franja, este renglón y
          el panel que había acá— no informaban tres veces mejor; empujaban la
          lista fuera de la pantalla y hacían dudar de si eran tres problemas. */}
      {error && !sinServidor && (
        <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="works-error">
          {error}
        </p>
      )}

      {/* El encabezado de la lista y nada más. Entre el mes y los trabajos no
          va nada: cada cosa que se pone acá empuja la lista fuera de la
          pantalla, y la lista es para lo que se entra. */}
      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          {/* Mientras se está trayendo la lista NO se dice «0 trabajos».
              Cero es un dato, y mientras no llegó la respuesta es falso: el mes
              puede tener veinte. Es la misma regla que en todo lo demás de la
              aplicación —no saber no es cero— y acá además se veía: al abrir
              la pantalla parpadeaba un «0 trabajos» antes de la lista. */}
          <h2 className="font-medium" data-testid="works-count">
            {esperandoLaLista ? (
              'Cargando…'
            ) : (
              <>
                {works.length + enCola.length} trabajo
                {works.length + enCola.length === 1 ? '' : 's'}
              </>
            )}
          </h2>
          {!esperandoLaLista && (
            <span className="text-sm text-[var(--tinta-suave)]" data-testid="works-total">
              Total <strong>{formatCents(totales.suma)}</strong>
              {/* «sin precio» y no «pendiente»: en esta pantalla ahora hay
                  trabajos pendientes DE SINCRONIZAR, y la misma palabra para dos
                  cosas distintas al lado de un importe se lee mal. */}
              {totales.sinPrecio > 0 && (
                <span className="ml-1 text-[var(--aviso)]">+ {totales.sinPrecio} sin precio</span>
              )}
            </span>
          )}
        </div>

        {/* La cola sube sola cuando vuelve la señal. Este botón existe para el
            caso en que alguien no quiere esperar a que eso pase: un renglón,
            y sólo cuando hay señal y hay algo que subir. */}
        {!sinServidor && enCola.some((a) => a.estado !== 'error') && (
          <button
            type="button"
            onClick={() => void sincronizar()}
            disabled={sincronizando}
            className="self-start text-sm font-medium text-[var(--accion)] underline disabled:opacity-50"
            data-testid="sincronizar-ahora"
          >
            {sincronizando ? 'Sincronizando…' : 'Sincronizar ahora'}
          </button>
        )}

        {cargando && <p className="text-sm text-[var(--tinta-suave)]">Cargando…</p>}

        {!cargando && works.length === 0 && enCola.length === 0 ? (
          <p className="text-sm text-[var(--tinta-suave)]" data-testid="no-works">
            No hay trabajos que se facturen en {MESES[month - 1]} {year}.
          </p>
        ) : (
          <ul className="flex flex-col gap-2" data-testid="works-list">
            {/* Primero los que faltan subir. Son de hoy —se acaban de cargar—
                y son los únicos que piden algo de quien mira. */}
            {enCola.map((a) => (
              <TarjetaPendiente
                key={a.clientRequestId}
                alta={a}
                onReintentar={() => void reintentar(a.clientRequestId)}
              />
            ))}
            {works.map((w) => (
              <WorkCard
                key={w.id}
                work={w}
                ocupado={ocupado}
                colorFacturador={colorDe(
                  gente.find((g) => g.id === w.biller_participant_id)?.color,
                )}
                // Los dos cambios de estado se confirman. Facturar porque
                // congela el trabajo; cobrar porque es decir que entró plata, y
                // decirlo sin querer es tan molesto como lo otro.
                onFacturar={(id) => setFacturando(works.find((x) => x.id === id) ?? null)}
                onCobrar={(id) => setCobrando(works.find((x) => x.id === id) ?? null)}
                onDeshacerCobro={(id) =>
                  void hacer(() => setWorkStatus(id, { isPaid: false }), 'Cobro deshecho.')
                }
                onRevertir={(id) => {
                  setMotivoRevertir('');
                  setRevirtiendo(works.find((x) => x.id === id) ?? null);
                }}
                onVerDetalle={onVerDetalle}
                onEditar={(id) => setForm({ mode: 'edit', workId: id })}
                onEliminar={(id) => setBorrando(works.find((x) => x.id === id) ?? null)}
                onAnular={(id) => void anular(id)}
                onDuplicar={(id) => setForm({ mode: 'duplicate', workId: id })}
              />
            ))}
          </ul>
        )}

        {/* Sugerencia, no automatismo: el mes no cambia solo. Aparece cuando ya
            no queda nada por facturar, que es cuando de verdad se pasa al
            siguiente. */}
        {!cargando && works.length > 0 && totales.facturados === works.length && (
          <p className="text-sm text-[var(--exito)]" data-testid="mes-facturado">
            Todo {MESES[month - 1]} está facturado.
          </p>
        )}
      </section>

      {/* Marcar facturado NO borra nada, así que no se pregunta con el diálogo
          de borrado ni en rojo. Lo que sí hay que decir antes es que a partir
          de acá el trabajo queda congelado. */}
      {facturando && (
        <ConfirmarAccion
          titulo="Marcar como facturado"
          pregunta="¿Confirmás que este trabajo ya fue facturado?"
          detalle={`${facturando.client_name} · ${formatCents(facturando.total_cents)}. Sus datos económicos quedarán bloqueados.`}
          confirmar="Confirmar"
          ocupado={ocupado}
          onCancelar={() => setFacturando(null)}
          onConfirmar={() => {
            const w = facturando;
            setFacturando(null);
            void hacer(() => setWorkStatus(w.id, { isBilled: true }));
          }}
          testId="confirmar-facturar"
        />
      )}

      {cobrando && (
        <ConfirmarAccion
          titulo="Marcar como cobrado"
          pregunta="¿Confirmás que ya entró el pago de este trabajo?"
          detalle={`${cobrando.client_name} · ${formatCents(cobrando.total_cents)}. No cambia ningún importe.`}
          confirmar="Confirmar"
          ocupado={ocupado}
          onCancelar={() => setCobrando(null)}
          onConfirmar={() => {
            const w = cobrando;
            setCobrando(null);
            void hacer(() => setWorkStatus(w.id, { isPaid: true }));
          }}
          testId="confirmar-cobrar"
        />
      )}

      {/* Se dice qué se lleva puesto ANTES, con nombre y número. «¿Estás
          seguro?» no es un dato; «el trabajo de La Esperanza del 14/08, por
          $ 100.000, ya facturado» sí lo es. */}
      {borrando && (
        <ConfirmarBorrado
          que={`el trabajo de ${borrando.client_name} del ${new Date(
            `${borrando.work_date}T00:00:00`,
          ).toLocaleDateString('es-AR')}`}
          detalle={[
            formatCents(borrando.total_cents),
            borrando.closure_id ? 'estuvo en una liquidación, que conserva sus números' : null,
          ]
            .filter(Boolean)
            .join(' · ')}
          ocupado={ocupado}
          onCancelar={() => setBorrando(null)}
          onConfirmar={() => {
            const w = borrando;
            setBorrando(null);
            void hacer(() => deleteWork(w.id), 'Trabajo eliminado.');
          }}
          testId="confirmar-borrar-trabajo"
        />
      )}

      {/* Revertir deshace algo que ya salió, así que se confirma. Confirmar no
          es lo mismo que justificar: se pregunta si va, no por qué. */}
      {revirtiendo && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-label="Revertir la facturación"
          onClick={() => setRevirtiendo(null)}
          data-testid="confirmar-revertir"
        >
          <div
            className="zona-inferior margen-lateral-seguro w-full max-w-2xl rounded-t-2xl border-t border-[var(--linea)] bg-[var(--superficie)] p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--linea-fuerte)]"
              aria-hidden
            />
            <h2 className="text-base font-medium">
              Revertir la facturación de {revirtiendo.client_name}
            </h2>
            <p className="mt-1 text-sm text-[var(--tinta-suave)]">
              El trabajo vuelve a estar editable. Queda registrado quién lo revirtió y cuándo.
            </p>
            {/* El motivo dejó de ser obligatorio: exigirlo sólo lograba que se
                escribiera cualquier cosa para poder seguir, y eso ensucia la
                bitácora en vez de documentarla. Lo que sirve para reconstruir
                qué pasó —quién y cuándo— se registra solo. */}
            <label className="mt-3 flex flex-col gap-1 text-sm">
              Motivo (opcional)
              <input
                value={motivoRevertir}
                onChange={(e) => setMotivoRevertir(e.target.value)}
                className="min-h-12 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
                placeholder="Si querés dejar anotado por qué"
                data-testid="motivo-revertir"
              />
            </label>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setRevirtiendo(null)}
                className="min-h-12 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] font-medium"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={ocupado}
                onClick={() => {
                  const w = revirtiendo;
                  const motivo = motivoRevertir.trim();
                  setRevirtiendo(null);
                  void hacer(
                    () => revertirFacturacion(w.id, motivo),
                    'Facturación revertida. El trabajo vuelve a estar editable.',
                  );
                }}
                className="min-h-12 flex-1 rounded-[var(--radius-control)] bg-[var(--accion)] font-medium text-[var(--sobre-accion)] disabled:opacity-50 "
                data-testid="revertir-confirmar"
              >
                Revertir
              </button>
            </div>
          </div>
        </div>
      )}

      <AvisoFlotante texto={aviso} onCerrar={() => setAviso(null)} />
    </div>
  );
}
